/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, BoxView, ActionButton, Text, TextField } from '@components/atoms'
import { OrderDetail } from '@components/organisms'
import { OrderInfoSection, OrderDeleteConfirmationModal } from '@components/molecules'
import { OrderList, OrderGrid } from '@components/systems'
import {
	Select,
	SelectChangeEvent,
	MenuItem,
	FormControl,
	InputLabel,
} from '@mui/material'

import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { useAppDispatch, useAppSelector, orderActions } from '@redux/index'
import { OrderType, OrderDetailType } from '@schemas'
import dayjs from 'dayjs'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { grey } from '@mui/material/colors'

const OrderPage = () => {
	const reduxDispatch = useAppDispatch()

	// redux state
	const selectedOrder: OrderType = useAppSelector(state => state.order.selectedOrder)
	const searchStartDate: string = useAppSelector(state => state.order.searchStartDate)
	const searchEndDate: string = useAppSelector(state => state.order.searchEndDate)
	const searchStatus: string = useAppSelector(state => state.order.searchStatus)
	const searchKeyword: string = useAppSelector(state => state.order.searchKeyword)
	const viewTabIndex: number = useAppSelector(state => state.order.viewTabIndex)

	// state
	const [showModal, setShowModal] = React.useState(false)

	const [currentOrderDetails, setCurrentOrderDetails] = React.useState<OrderDetailType[]>(
		[],
	)

	React.useEffect(() => {
		if (selectedOrder) {
			setCurrentOrderDetails(JSON.parse(JSON.stringify(selectedOrder.detailList)))
		}
	}, [selectedOrder])

	const onSearchClick = () => {
		reduxDispatch(orderActions.TRIGGER_ORDER_SEARCH(true))
	}

	const onStartDateChange = (date: any) => {
		const newDate = dayjs(date).format('YYYY-MM-DD')
		reduxDispatch(orderActions.SEARCH_START_DATE(newDate))
	}

	const onEndDateChange = (date: any) => {
		const newDate = dayjs(date).format('YYYY-MM-DD')
		reduxDispatch(orderActions.SEARCH_END_DATE(newDate))
	}

	const onStatusChange = (e: SelectChangeEvent) => {
		const newStatus = e.target.value
		reduxDispatch(orderActions.SEARCH_STATUS(newStatus))
	}

	const onKeywordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const str = e.target.value
		reduxDispatch(orderActions.SEARCH_KEYWORD(str))
	}

	const onDeleteClick = (anOrder: OrderType) => {
		reduxDispatch(orderActions.SELECTED_ORDER(anOrder))
		setShowModal(true)
	}

	const onModalDeleteClick = () => {
		if (selectedOrder) {
			reduxDispatch(orderActions.DELETE_ORDER({ orderId: selectedOrder.id }))
			setShowModal(false)
		}
	}

	return (
		<View css={style}>
			{viewTabIndex === 0 ? (
				<>
					<View className="orderOuterView">
						<BoxView
							title="주문 현황"
							className="orderView"
							rightHeaderAccessory={
								<View className="orderListHeaderAccessoryView">
									<View className="orderDateOptionView">
										<ActionButton
											className="searchBtn"
											tooltip="조회"
											iconName="check-bold"
											variant="contained"
											buttonColor="primary"
											onClick={onSearchClick}
										/>

										<LocalizationProvider dateAdapter={AdapterDateFns}>
											<View className="datePickerView">
												<DatePicker
													className="datePicker"
													label="시작일자"
													value={new Date(searchStartDate)}
													format="yyyy-MM-dd"
													onChange={onStartDateChange}
													slotProps={{
														textField: {
															variant: 'outlined',
															size: 'small',
															style: {
																width: '8rem',
															},
															placeholder: 'YYYY-MM-DD',
														},
													}}
												/>
												<Text className="datePickerText">~</Text>
												<DatePicker
													className="datePicker"
													label="종료일자"
													format="yyyy-MM-dd"
													value={new Date(searchEndDate)}
													onChange={onEndDateChange}
													slotProps={{
														textField: {
															variant: 'outlined',
															size: 'small',
															style: {
																width: '8rem',
															},
															placeholder: 'YYYY-MM-DD',
														},
													}}
												/>
											</View>
										</LocalizationProvider>
									</View>
								</View>
							}
						>
							<View className="orderViewContent">
								<View className="orderViewContentHeader">
									<TextField
										type="text"
										label="검색"
										size="small"
										value={searchKeyword}
										onChange={onKeywordChange}
									/>
									<FormControl fullWidth>
										<InputLabel id="delivery-state-select-label">상태</InputLabel>
										<Select
											className="categSelect"
											name="categ"
											label="상태"
											value={searchStatus}
											size="small"
											onChange={onStatusChange}
										>
											<MenuItem value="all">전체</MenuItem>
											<MenuItem value="new">신규 주문</MenuItem>
											<MenuItem value="preparing">배송 준비중</MenuItem>
											<MenuItem value="inTransit">배송중</MenuItem>
											<MenuItem value="completed">배송완료</MenuItem>
											<MenuItem value="deleted">삭제됨</MenuItem>
										</Select>
									</FormControl>
								</View>
								<OrderList
									onDeleteClick={onDeleteClick}
									onTabClick={(index: number) =>
										reduxDispatch(orderActions.VIEW_TAB_INDEX(index))
									}
								/>
							</View>
						</BoxView>
						<BoxView title="주문 상세" className="orderDetailView">
							{selectedOrder ? (
								<View className="orderDetailContent">
									<OrderInfoSection />
									<OrderDetail orderDetails={currentOrderDetails} />
								</View>
							) : (
								<p>주문을 선택해주세요</p>
							)}
						</BoxView>
					</View>

					<OrderDeleteConfirmationModal
						open={showModal}
						onClose={() => setShowModal(false)}
						onDelete={onModalDeleteClick}
						orderId={selectedOrder ? selectedOrder.id : ''}
					/>
				</>
			) : (
				<OrderGrid />
			)}
		</View>
	)
}

const style = css({
	'.orderOuterView': {
		position: 'relative',
		display: 'flex',

		'.orderListHeaderAccessoryView': {
			'.orderDateOptionView': {
				display: 'flex',
				height: '1.6rem',
				width: '100%',

				'.datePickerView': {
					display: 'flex',
					alignItems: 'center',
					marginBottom: '-0.3rem',

					'.datePicker': {
						width: '9rem',

						'.MuiInputBase-input': {
							fontSize: '11px',
						},
					},

					'.datePickerText': {
						margin: '1.5rem 0.5rem',
					},
				},
			},
		},

		'.orderView': {
			width: '30rem',
			marginRight: '0.5rem',
			paddingLeft: '2rem',
			position: 'relative',

			'.orderViewContent': {
				display: 'flex',
				flexDirection: 'column',
				width: '100%',
				height: '100%',

				'.orderViewContentHeader': {
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'flex-end',
					gap: '0.5rem',
					width: '100%',
					marginBottom: '0.5rem',
				},

				'.listOuterView': {
					width: '100%',
					height: '100%',
					paddingTop: '5px',
					paddingBottom: '5px',
					border: '1px solid',
					borderColor: grey[300],
					borderRadius: '5px',
					backgroundColor: grey[50],
					overflowY: 'auto',
					padding: '0.5rem 1rem',
				},
			},
		},
		'.orderDetailView': {
			width: 'calc(100% - 30rem)',
			position: 'relative',

			'.orderDetailContent': {
				height: '100%',
				width: '100%',
				padding: '0.5rem',
				background: grey[50],
				border: '1px solid',
				borderColor: grey[300],
				borderRadius: '5px',
				overflowY: 'auto',

				'.orderInfo': {
					display: 'flex',
					flexDirection: 'column',
					gap: '1rem',
					padding: '1rem',
					backgroundColor: '#f8f9fa',
					borderRadius: '0.5rem',

					'.infoRow': {
						display: 'flex',
						gap: '2rem',

						'.infoItem': {
							flex: 1,
							display: 'flex',
							gap: '0.5rem',

							'.label': {
								color: '#6b7280',
								fontWeight: 500,
							},

							'.value': {
								color: '#111827',
							},
						},
					},
				},
			},
		},
	},
})

export default OrderPage
